import React from 'react';
import { Button, Box, Typography } from '@material-ui/core';
import { 
  Theme,
  CustomArrowBackIcon,
  SnackBar,
  Loader,
  SelectedMembersSection,
  SettingsSection,
} from '@orijinworks/frontend-commons';

// MODAL_CONTEXTS constant (since it's not exported from package)
const MODAL_CONTEXTS = {
  CHANNEL: 'channel',
  ANNOUNCEMENT: 'announcement',
  MESSAGE: 'message',
  EDIT: 'edit',
};

import MemberAutocomplete from './MemberAutocomplete';
import { useAdminLearnerModalHook } from './useAdminLearnerModalHook';
import {
  mainContainer,
  SnackBarComponent,
  headerContainer,
  closeButton,
  headerTitle,
  headerTextContainer,
  contentContainer,
  membersSection,
  autocompleteSpacing,
  footerContainer,
  getCreateButtonStyles,
  cancelButton,
  errorMessage,
} from './AdminLearnerModal.styles';

const AdminLearnerModal = ({
  onClose,
  onCreateConversation,
  isCreateLoading = false,
  showDisplayNamesOnly = false,
  initialConversationSettings = {},
}) => {
  const {
    // Admin autocomplete props
    adminOptions,
    selectedAdmin,
    selectedAdminObject,
    adminInputValue,
    isAdminSearchLoading,
    handleAdminChange,
    handleAdminSearchChange,

    // Learner autocomplete props
    learnerOptions,
    selectedLearners,
    isLearnerSearchLoading,
    handleLearnersChange,
    handleLearnerSearchChange,

    // Conversation settings
    learnerCanSendMessages,
    limitLearnerMessages,
    numberOfMessages,
    shutDownConversation,
    setLearnerCanSendMessages,
    setLimitLearnerMessages,
    setNumberOfMessages,
    setShutDownConversation,

    // Validation and actions
    memberSelectionError,
    handleCreateConversation,
    getOptionLabel,
    filterOptions,
    isOptionEqualToValue,
    getButtonDisabledState,

    // Close handler
    handleCloseKeyDown,

    // Selected members section props
    allSelectedMembers,
    handleRemoveMember,
    allMemberOptions,
  } = useAdminLearnerModalHook({
    onCreateConversation,
    initialConversationSettings,
    isCreateLoading,
    onClose,
  });

  return (
    <Theme>
      <Box sx={mainContainer}>
        <Box sx={SnackBarComponent}>
          <SnackBar hideCrossIcon={true} />
        </Box>

        {/* Header */}
        <Box sx={headerContainer}>
          <Box
            onClick={onClose}
            sx={closeButton}
            role="button"
            aria-label="close"
            tabIndex={0}
            onKeyDown={handleCloseKeyDown}
          >
            <CustomArrowBackIcon size={24} fill="#0F48B8" />
          </Box>
          <Box sx={headerTextContainer}>
            <Typography variant="h6" style={headerTitle}>
            Create a Conversation for Another Admin
            </Typography>
          </Box>
        </Box>

        {/* Content Area */}
        <Box sx={contentContainer}>
          {/* Members Section */}
          <Box sx={membersSection}>
            {/* Admin Autocomplete */}
            <Box sx={autocompleteSpacing}>
              <MemberAutocomplete
                rootClassName="admin-autocomplete-root"
                multiple={false}
                disabled={!!selectedAdmin}
                label="Select Facility Admin"
                placeholder="Search Facility Admin"
                memberOptions={adminOptions}
                selectedMembers={selectedAdmin}
                inputValue={adminInputValue}
                onMembersChange={handleAdminChange}
                onSearchChange={handleAdminSearchChange}
                isMemberListLoading={isAdminSearchLoading}
                showDisplayNamesOnly={showDisplayNamesOnly}
                filterOptions={filterOptions}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
              />
            </Box>

            {/* Learner Autocomplete */}
            <Box sx={autocompleteSpacing}>
              <MemberAutocomplete
                rootClassName="learner-autocomplete-root"
                multiple={true}
                disabled={!selectedAdmin || selectedLearners.length > 0}
                label="Select Learners"
                placeholder="Search Learners"
                helperText={
                  !selectedAdmin 
                    ? "Please select a facility admin first" 
                    : selectedLearners.length > 0 
                    ? "Learners selected. Remove learners to select different ones." 
                    : ""
                }
                memberOptions={learnerOptions}
                selectedMembers={selectedLearners}
                onMembersChange={handleLearnersChange}
                onSearchChange={handleLearnerSearchChange}
                isMemberListLoading={isLearnerSearchLoading}
                showDisplayNamesOnly={showDisplayNamesOnly}
                filterOptions={filterOptions}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
              />
            </Box>

            {/* Error message display */}
            {memberSelectionError && (
              <Box sx={errorMessage}>
                {memberSelectionError}
              </Box>
            )}

            {/* Selected Members Display Section */}
            <SelectedMembersSection
              selectedMembers={allSelectedMembers}
              memberOptions={allMemberOptions}
              onRemoveMember={handleRemoveMember}
              hideCountMessage={true}
            />
          </Box>

          {/* Settings Section */}
          <SettingsSection
            modalContext={MODAL_CONTEXTS.MESSAGE}
            isSettingsEligible={true}
            learnerCanMessagesInFeed={false}
            learnerCanReplyInThread={false}
            limitLearnerReplies={false}
            channelNumberOfMessages={0}
            learnerCanSeeOthersResponses={false}
            learnerCanSendMessages={learnerCanSendMessages}
            limitLearnerMessages={limitLearnerMessages}
            numberOfMessages={numberOfMessages}
            shutDownConversation={shutDownConversation}
            setLearnerCanMessagesInFeed={() => {}}
            setLearnerCanReplyInThread={() => {}}
            setLimitLearnerReplies={() => {}}
            setChannelNumberOfMessages={() => {}}
            setLearnerCanSeeOthersResponses={() => {}}
            setLearnerCanSendMessages={setLearnerCanSendMessages}
            setLimitLearnerMessages={setLimitLearnerMessages}
            setNumberOfMessages={setNumberOfMessages}
            setShutDownConversation={setShutDownConversation}
          />
        </Box>

        {/* Footer with Action Buttons */}
        <Box sx={footerContainer}>
          {isCreateLoading ? (
            <Loader isLoading={true} />
          ) : (
            <>
              <Button
                variant="contained"
                style={getCreateButtonStyles(getButtonDisabledState())}
                disabled={getButtonDisabledState()}
                onClick={handleCreateConversation}
              >
                Create Conversation
              </Button>
              <Button variant="outlined" style={cancelButton} onClick={onClose}>
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Theme>
  );
};

export default AdminLearnerModal;
